import {Clone, GetEntries} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Text, Row, RowLR, Select, Spinner, TextInput} from "react-vcomponents";
import {InfoButton, RunInAction_Set, Slider, TextPlus} from "web-vcore";
import {ObjModifier} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifier.js";
import {DEFAULT_HYPSOMETRIC_STOPS, HypsometricStop, Mod_TerrainDefinition, PoissonSolverConfig, TerrainHeightColorGradient, TerrainHeightShadingConfig} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_TerrainDefinition.js";
import {TerrainDefinitionTab} from "../../../../../Store/main/maps/@MapState.js";
import {ColorPickerBox_Ext} from "../../../../../Utils/ReactComponents/ColorPickerBox_Ext.js";
import {SpinnerForPercent} from "../../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {ModifierUI_SharedProps} from "../Modifiers_CompUI.js";
import {terrainDataVersion, terrainPreparedData_current} from "../../../../../Terrain/Heights/TerrainRenderer.js";
import {sliderFlex} from "../../../../../Utils/UI/GlobalStyles.js";

const splitAt = 180;

export const TerrainDefinition_ModUI = observer_mgl((props: {mod: ObjModifier} & ModifierUI_SharedProps)=>{
	const {modWrapper: mod, ChangeModWrapper: ChangeMod, mapState} = props;
	const Change = (mutator: (data: Mod_TerrainDefinition)=>any)=>ChangeMod(a=>mutator(a.terrainDefinition!));
	const uiState = mapState.propertiesPanel;
	const data = mod.terrainDefinition!;

	const groupHeaderStyle = {marginTop: 15, fontSize: 16, fontWeight: "bold"};
	return (
		<>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					Unique identifier for this terrain layer.
					When multiple Terrain Definition modifiers exist on different objects, their terrainID must match a Terrain Region modifier's terrainID to control which area each definition applies to.
				`.AsMultiline(0)}>Terrain ID:</TextPlus>
				<TextInput value={data.terrainID} onChange={val=>Change(a=>a.terrainID = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					Master random seed for all terrain generation in this layer.
					Changing the seed reshuffles all procedurally-resolved isoline properties (elevation, shape, summit position, etc.) while keeping user-defined constraints and pins intact.
				`.AsMultiline(0)}>Seed:</TextPlus>
				<Spinner instant value={data.seed} onChange={val=>Change(a=>a.seed = val)}/>
			</RowLR>
			<Row mt={10} style={{fontWeight: "bold"}}>
				<Select displayType="button bar" options={GetEntries(TerrainDefinitionTab, "ui")}
					value={uiState.terrainDefinition_tab} onChange={val=>RunInAction_Set(()=>uiState.terrainDefinition_tab = val)}/>
			</Row>

			{uiState.terrainDefinition_tab == TerrainDefinitionTab.ambient && <>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Noise algorithm used for the ambient fractal.
						OpenSimplex2 (default): smooth, organic-looking terrain with no axis-aligned artifacts.
						Perlin: classic gradient noise, slightly more structured patterns.
						Value: blocky appearance, lower quality but faster.
					`.AsMultiline(0)}>Algorithm:</TextPlus>
					<Select displayType="button bar" options={["opensimplex2", "perlin", "value"]}
						value={data.ambient.algorithm} onChange={val=>Change(a=>a.ambient.algorithm = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						How multiple octaves of noise are combined.
						fBm: standard fractional Brownian motion — smooth hills and valleys (default).
						Ridged: inverts the noise to create sharp ridges and crevasses.
						Billow: absolute-value noise — produces puffy cloud-like mounds.
					`.AsMultiline(0)}>Fractal type:</TextPlus>
					<Select displayType="button bar" options={["fbm", "ridged", "billow"]}
						value={data.ambient.fractalType} onChange={val=>Change(a=>a.ambient.fractalType = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						World-space size (meters) of one full noise cycle — sets the scale of the broadest terrain features.
						Larger values produce wider, more gently-rolling hills; smaller values produce tighter, denser undulations.
						The ambient generator is meant to supply only gentle background relief; dramatic features come from isoline constraints.
						Typical range: 500-5000 m.
					`.AsMultiline(0)}>Wavelength (m):</TextPlus>
					<Spinner min={1} value={data.ambient.wavelength} onChange={val=>Change(a=>a.ambient.wavelength = Math.max(1, val))}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Number of noise layers (octaves) summed together.
						Each additional octave adds detail at half the wavelength of the previous one (modulated by lacunarity).
						More octaves = more fine detail, but also more computation. Typical range: 4-8.
					`.AsMultiline(0)}>Octaves:</TextPlus>
					<Spinner min={1} step={1} value={data.ambient.octaves} onChange={val=>Change(a=>a.ambient.octaves = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Amplitude multiplier applied to each successive octave (0-100%).
						50 (default): each octave contributes half the amplitude of the previous — detail fades quickly.
						Values near 100: all octaves contribute equally — very rough, fractal appearance.
						Values near 0: only the broadest octave is visible — very smooth.
					`.AsMultiline(0)}>Persistence:</TextPlus>
					<SpinnerForPercent min={1} value={data.ambient.persistence} onChange={val=>Change(a=>a.ambient.persistence = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Frequency multiplier applied to each successive octave (≥ 1).
						2.0 (default): each octave has twice the frequency (half the wavelength) of the previous — standard fBm.
						Higher values pack detail octaves closer together in frequency; lower values spread them out.
					`.AsMultiline(0)}>Lacunarity:</TextPlus>
					<Spinner step={0.1} min={0.5} max={8} value={data.ambient.lacunarity} onChange={val=>Change(a=>a.ambient.lacunarity = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Horizontal offset applied to noise sample coordinates (world-space X axis).
						Useful for panning the noise pattern without changing the seed or any other parameters.
					`.AsMultiline(0)}>Offset X:</TextPlus>
					<Spinner step={0.1} value={data.ambient.offset_x} onChange={val=>Change(a=>a.ambient.offset_x = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Horizontal offset applied to noise sample coordinates (world-space Y axis).
						Useful for panning the noise pattern without changing the seed or any other parameters.
					`.AsMultiline(0)}>Offset Y:</TextPlus>
					<Spinner step={0.1} value={data.ambient.offset_y} onChange={val=>Change(a=>a.ambient.offset_y = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						The elevation (meters) at the center of the ambient noise range — i.e. where "flat" terrain sits.
						The noise oscillates above and below this value by ± amplitude/2.
						Set to 0 for sea-level baseline. Negative values produce terrain that dips below sea level by default.
					`.AsMultiline(0)}>Base elevation (m):</TextPlus>
					<Spinner step={10} value={data.ambient.baseElevation} onChange={val=>Change(a=>a.ambient.baseElevation = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Total peak-to-peak relief of the ambient noise layer (meters).
						The noise ranges from baseElevation - amplitude/2 to baseElevation + amplitude/2 (the noise oscillates ± amplitude/2 around the base elevation).
						Keep this small (e.g. 30-100 m) — dramatic elevation changes should come from isoline constraints, not the ambient generator.
						Isoline RBF correction bends the ambient field to honor drawn contours, so a large amplitude here makes corrections harder to apply cleanly.
					`.AsMultiline(0)}>Amplitude (m):</TextPlus>
					<Spinner step={5} min={0} value={data.ambient.amplitude} onChange={val=>Change(a=>a.ambient.amplitude = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Maximum wavelength (meters) for an octave to be counted as part of the "detail band" (D).
						Octaves with wavelength <= this value are DETAIL (applied on top everywhere, and NEVER dampened near elevation pins); octaves with wavelength larger than this are LARGE-WAVE (the base relief, dampened near pins when "Dampen ambient waves near pins (large)" is on).
						Set it lower (e.g. 100) to treat only very-fine octaves as detail (more of the noise gets dampened near pins); set it higher (e.g. 500) to treat more octaves as detail (more noise stays visible near pins).
						Unset → falls back to wavelength/2 (the old behavior).
					`.AsMultiline(0)}>Max wavelength as detail (m):</TextPlus>
					<Spinner step={10} min={0} value={data.ambient.maxWavelengthAsDetail}
						onChange={val=>Change(a=>a.ambient.maxWavelengthAsDetail = val != null && val > 0 ? val : undefined)}/>
					<InfoButton ml={5} text={`
						Important note: You must set Algorithm to "value" to have the L-band (CPU) and D-band (GPU) noises align.
						Also: Detail normal strength needs to be ~0.2, IF you want to approximately match their "intensities". (varies by context)
					`.AsMultiline(0)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Gain for the fragment-shader ambient detail normal (Set10). The fine octaves that the height
						lattice cannot represent (wavelength < ~4× the chunk's lattice spacing) are evaluated in the
						fragment shader at pixel scale instead. This knob sets how strong that relief reads.
						1 (default) = the physically-consistent share (the octaves' true amplitudes); higher values
						exaggerate fine relief so it reads on hillsides. 0 disables the shader band entirely.
						Note: the band's slope is scaled by "Slope scale" too, so raising that also raises fine detail.
					`.AsMultiline(0)}>Detail normal strength:</TextPlus>
					<Spinner step={0.1} min={0} max={10} value={data.ambient.detailNormalStrength}
						onChange={val=>Change(a=>a.ambient.detailNormalStrength = val != null && val >= 0 ? val : undefined)}/>
				</RowLR>
			</>}

			{uiState.terrainDefinition_tab == TerrainDefinitionTab.solver && data.constraintSettings.poissonSolver && <>
				<Row style={groupHeaderStyle}>Constraint settings (V3 Poisson)</Row>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Sample-spacing factor for the ambient-noise L/D split threshold (legacy forward-compat).
						The actual V3 working-grid cell size comes from the renderer's terrain.solverFineCell_sizeAsPx
						projection; this knob is only used to derive the ambient octaves' L vs D split. Typical: 1/40.
					`.AsMultiline(0)}>Sample spacing factor:</TextPlus>
					<Spinner step={0.001} min={0.001} value={data.constraintSettings.sampleSpacingFactor} onChange={val=>Change(a=>a.constraintSettings.sampleSpacingFactor = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Minimum ambient sample spacing (meters). Default 20.
					`.AsMultiline(0)}>Sample spacing min (m):</TextPlus>
					<Spinner step={1} min={1} value={data.constraintSettings.sampleSpacingMin} onChange={val=>Change(a=>a.constraintSettings.sampleSpacingMin = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Maximum ambient sample spacing (meters). Default 80.
					`.AsMultiline(0)}>Sample spacing max (m):</TextPlus>
					<Spinner step={1} min={1} value={data.constraintSettings.sampleSpacingMax} onChange={val=>Change(a=>a.constraintSettings.sampleSpacingMax = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
							Detail-damping band scaling constant (default 2.5). V3 builds w(p) from the distance to the
							nearest rasterized constraint cell on the AMR grid (Poisson/TwoGridCache.buildDetailDamping).
						This knob tunes the default band width (4 × fine cell size × dampingK).
					`.AsMultiline(0)}>Damping K:</TextPlus>
					<Spinner step={0.1} min={0} value={data.constraintSettings.dampingK} onChange={val=>Change(a=>a.constraintSettings.dampingK = val)}/>
				</RowLR>
				<RowLR mt={8} splitAt={splitAt}>
					<TextPlus info={`
						V3 Hnaidi-Poisson + AMR solver configuration. When present, the terrain uses the V3 two-grid
						solve (macro grid + camera-window working grid). When absent the engine builds a default
						PoissonSolverConfig at runtime. Use this block to override outer-BC mode, vCycle budget,
						Neumann/barrier gating, and per-feature default cell size.
					`.AsMultiline(0)}>Poisson solver (V3):</TextPlus>
					<CheckBox value={data.constraintSettings.poissonSolver != null} onChange={val=>Change(a=>{
						a.constraintSettings.poissonSolver = val ? new PoissonSolverConfig() : undefined;
					})}/>
				</RowLR>

				<Row style={groupHeaderStyle}>Solver (general)</Row>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus>V-Cycles:</TextPlus>
					<Spinner min={1} step={1} value={data.constraintSettings.poissonSolver.vCycles} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.vCycles = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus>Pre-smooth sweeps:</TextPlus>
					<Spinner min={1} step={1} value={data.constraintSettings.poissonSolver.preSmoothSweeps} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.preSmoothSweeps = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus>Post-smooth sweeps:</TextPlus>
					<Spinner min={1} step={1} value={data.constraintSettings.poissonSolver.postSmoothSweeps} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.postSmoothSweeps = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus>Residual tolerance:</TextPlus>
					<Spinner step={1e-4} min={0} value={data.constraintSettings.poissonSolver.residualTolerance} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.residualTolerance = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus>Coarse direct-solve threshold:</TextPlus>
					<Spinner min={1} step={1} value={data.constraintSettings.poissonSolver.coarseDirectSolveThreshold} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.coarseDirectSolveThreshold = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus>Max AMR refinement depth:</TextPlus>
					<Spinner min={1} step={1} value={data.constraintSettings.poissonSolver.maxRefinementDepth} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.maxRefinementDepth = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Set8 — per-octave LARGE-wave muting near elevation pins (the reworked ambient detail).
						When on, each ambient wave is suppressed within ~its own wavelength of an elevation pin:
						the large (low-frequency) waves are muted near pins so pin target elevations stay EXACT,
						while detail (high-frequency) waves — whose wavelength is tiny — are practically never muted.
						Each wave re-emerges beyond ~one wavelength from the pin. Off = the full ambient layer is
						applied everywhere, uncorrupted near pins (pins may not stay exact).
					`.AsMultiline(0)}>Dampen ambient waves near pins (large):</TextPlus>
					<CheckBox value={data.constraintSettings.poissonSolver.dampenAmbientWaves_large} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.dampenAmbientWaves_large = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus>Enable Neumann (slope) BCs:</TextPlus>
					<CheckBox value={data.constraintSettings.poissonSolver.enableNeumann} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.enableNeumann = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus>Enable barrier (no-flux) cells:</TextPlus>
					<CheckBox value={data.constraintSettings.poissonSolver.enableBarriers} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.enableBarriers = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus>Outer BC mode:</TextPlus>
					<Select displayType="button bar" options={["ambient", "flat", "neumann_zero"]}
						value={data.constraintSettings.poissonSolver.outerBC} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.outerBC = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus>Outer flat elevation (m):</TextPlus>
					<Spinner step={10} value={data.constraintSettings.poissonSolver.outerFlatElevation} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.outerFlatElevation = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus>Default constraint cell size (m):</TextPlus>
					<Spinner step={0.5} min={0.5} value={data.constraintSettings.poissonSolver.defaultConstraintCellSize} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.defaultConstraintCellSize = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Cell size TARGET (in metres) of the coarse macro grid that covers the entire world and provides the boundary values for the working (camera-window) grid AND the ring stack (ring K pins from it).
						When unset (or 0), defaults to max(worldExtent / 20, fineCellSize * 2) — i.e. ~1/20 of the larger world dimension.
						Lowering this lets the macro grid resolve features that are close together (the default can blur two rings within ~macroCellSizeTarget/2 of each other onto the same macro cell, so the inner ring's elevation leaks toward the outer ring's — the "inner lake floor drifts up to 0 on close-zoom" symptom).
						Cost scales as (worldExtent / macroCellSizeTarget)^2 — the macro grid covers the whole world, so halving this 4x's the macro-leaf count.
						Typical values: 100-500m for maps with widely-separated features; 25-100m for tightly-nested lake-isoline maps.

						**This value is a TARGET / UPPER-BOUND, not the precise final cell size.**  The macro grid's quadtree is constrained to dyadic (power-of-two) subdivisions of the world's root size, so the actual realised cell size is rootSize / 2^level where level = ceil(log2(rootSize / macroCellSizeTarget)) — i.e. the nearest power-of-two fraction of rootSize that is <= macroCellSizeTarget.  When rootSize / macroCellSizeTarget lands just above a power-of-two boundary, the realised cell size is HALF the requested value (e.g. requesting 200m on a world whose root size makes the ratio just above a power-of-two yields 100m cells).  The realised value (after the dyadic snap) is shown to the right of the spinner as an arrow followed by the value in metres, once the macro grid has been (re)solved; while the macro solve is in flight or the cache has not yet been built, the annotation is hidden.
					`.AsMultiline(0)}>Macro cell size (m; 0 = auto):</TextPlus>
					<Spinner step={5} min={0} value={data.constraintSettings.poissonSolver.macroCellSizeTarget}
						onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.macroCellSizeTarget = val != null && val > 0 ? val : undefined)}/>
					{(()=>{
						const _v = terrainDataVersion.get(); void _v; // reactivity hook
						const prepared = terrainPreparedData_current.values().next().value as
							| {realizedMacroCellSize?: number; macroCellSizeTarget?: number}
							| undefined;
						if (prepared?.realizedMacroCellSize == null) return null;
						return <Text ml={5} style={{fontSize: 12, opacity: 0.7}}>→ {prepared.realizedMacroCellSize.toFixed(1)}m</Text>;
					})()}
				</RowLR>

				<Row style={groupHeaderStyle}>2D mode</Row>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>2D fine-cell size (m):</Text>
					<TextPlus info={`
						The 2D mode's fine-cell size is controlled by the setting at:
						* Map settings -> Terrain -> Solver fine-cell size (in 2d mode)
					`.AsMultiline(0)}>{`(see map settings)`}</TextPlus>
				</RowLR>

				<Row style={groupHeaderStyle}>3D mode</Row>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						3D WORKING-GRID radius (metres) — the radius around the solve centre (the eye + the
						viewpoint's detailed-solve-centre offset) within which the terrain is solved at high
						detail.  Only applies when the ring-stack solver is DISABLED (classic two-grid path):
						the ring stack sizes its own near field = mode3D_fineCellSize × ringCellsPerSide.
						The perspective cameraView is otherwise the whole horizon footprint; capping the
						solve avoids the leaf-budget blowup.  Default 1000.
					`.AsMultiline(0)}>Working-grid radius (m):</TextPlus>
					<Spinner min={0} step={100} value={data.constraintSettings.poissonSolver.mode3D_workingGridRadius}
						onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.mode3D_workingGridRadius = Math.max(0, val))}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						3D fine-cell size (metres), independent of eye distance.  A high viewpoint would
						otherwise solve cells too coarse to resolve near-field peaks.  This is the requested
						solve cell size in 3D mode (ignored in 2D — the 2D fine cell comes from the
						renderer's mode-2D knob).  It is also the ring-0 target under the ring stack.
						Default 2.
					`.AsMultiline(0)}>Fine-cell size (m):</TextPlus>
					<Spinner step={0.5} value={data.constraintSettings.poissonSolver.mode3D_fineCellSize}
						onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.mode3D_fineCellSize = Math.max(0.1, val))}/>
				</RowLR>

				<Row style={groupHeaderStyle}>Working-grid solver</Row>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Width (in metres) of the rasterization band added around the free-solve rect (i.e. around camera-view + fine-solve-padding).
						Features whose geometry falls inside this band get rasterized into the working grid, so barriers / feature-Dirichlets straddling the solve boundary get pinned rather than leaking.
						Cells in this band that aren't barriers/feature-Dirichlets are macro-Dirichlet-pinned (overwritten with macro H), so for the solved field this band is mostly inert.
						Its real job is housing the barrier/feature-Dirichlet cells of features that straddle the edge.
						Measured in metres (not a view-percent) because it's a geometry-granularity margin (a cliff is ~2m thick regardless of zoom).
						Default 25m — enough for barrier-cell coverage without bloating close-zoom working grids.
					`.AsMultiline(0)}>Rasterization padding (m):</TextPlus>
					<Spinner step={5} min={0} value={data.constraintSettings.poissonSolver.rasterizationPadding} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.rasterizationPadding = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						What fraction (0%-100%) of the camera view's larger dimension (max(width, height)) is kept *free* in the fine solve, expanding the free-solve region outward from the visible camera view.
						The macro-pinned ring (the coarse↔fine BC handoff) sits at the outer edge of the free band, i.e. (max(viewW, viewH) × this%) metres from the visible rect.
						Raising this pushes the handoff outward from what the user can see, so any macro-vs-fine resolution discrepancy decays (~1/d for Laplace) across the free band before reaching the visible rect — reducing edge morphing/flux on pan.
						Scales with zoom (a 10m room gets a ~5m band at 50%, a 5km continent gets ~2.5km — the free-region-to-viewport ratio stays constant across zoom)...
						...AND stays stable when the viewport aspect ratio changes (e.g. opening bottom-anchored devtools shrinks the view height drastically, but "max" keeps the band anchored to the still-wide axis, so panning along the wide axis doesn't suddenly morph at the edges).
						The cost is that the short axis is over-padded; the maxWorkingGridLeafCount budget clamp absorbs pathological close-zoom cases.
						Default 50% = the knee of the stability/cost curve.
						0% = ring right at the camera view's edge (max morphing, historical behaviour); raise toward 100% for more stability at proportionally more free cells.
					`.AsMultiline(0)}>Fine-solve padding (% of max view-dim):</TextPlus>
					<SpinnerForPercent step={5} min={0} value={data.constraintSettings.poissonSolver.fineSolvePaddingPercent} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.fineSolvePaddingPercent = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Extra margin (as a fraction of the camera view's larger dimension) used by the per-isoline "mute dominance" gate.
						For isolines that set "Mute dominance when" (in the path's terrain-feature panel), the terrain system builds a *second* macro-grid variant with that isoline's dominance disabled, used only when the camera viewport (padded by this margin) is fully inside OR fully outside the isoline — so inner water rings can pull the macro H on shared macro cells instead of being blocked by a high-dominance coastline straddling the same macro cell (the "sand in water on close-zoom" bug).
						The margin keeps the gate from flickering as the camera crosses the coastline; 10% is a modest default. 0 = gate fires exactly at the polygon edge (may flap).  No effect when no isoline sets "Mute dominance when".
					`.AsMultiline(0)}>Preserve-dominance padding (% of max view-dim):</TextPlus>
					<SpinnerForPercent step={5} value={data.constraintSettings.poissonSolver.preserveDominancePaddingPercent} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.preserveDominancePaddingPercent = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Same purpose as the percent-based preserve-dominance padding above, but in metres so it stays constant across zoom (at very close zoom a 10% margin can collapse to a sub-metre sliver that doesn't actually clear the coastline; this knob gives a zoom-independent floor).
						If both this and the percent value are set, the actual padding used is the max(percent result, this metres value). If only one is set, that one is used. Typical use: set the percent to 10% AND this to ~25-50m so the mute fires clearly inside/outside the coast regardless of zoom.
						0 = ignored (falls back to the percent value alone). No effect when no isoline sets "Mute dominance when".
					`.AsMultiline(0)}>Preserve-dominance padding (m; zoom-independent floor):</TextPlus>
					<Spinner value={data.constraintSettings.poissonSolver.preserveDominancePaddingMeters} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.preserveDominancePaddingMeters = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus>Max working-grid leaf count:</TextPlus>
					<Spinner min={1024} step={4096} value={data.constraintSettings.poissonSolver.maxWorkingGridLeafCount} onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.maxWorkingGridLeafCount = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Force a uniform-level working grid by clamping every feature's cellSize to be no smaller than the base cell size.
						Note: This is almost always needed, for the full GPU solver to be able to be used.
					`.AsMultiline(0)}>Force uniform level working grid (loses fine-feature sharpness):</TextPlus>
					<CheckBox value={data.constraintSettings.poissonSolver.forceUniformLevelWorkingGrid ?? false}
						onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.forceUniformLevelWorkingGrid = val)}/>
				</RowLR>

				<Row style={groupHeaderStyle}>Ring-stack solver</Row>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Ring-stack solve for 2D panning: K nested uniform-level rings (cell size c0·2^k)
						from the camera to the world edge, blended across the §3.2 band.  Fixed power-of-two
						cell sizes coalesce the pan/zoom cache.  Consults this flag ONLY in 2D mode (3D uses
						the 3D checkbox above).  Default off (single working grid).
					`.AsMultiline(0)}>Ring-stack solve (2D):</TextPlus>
					<CheckBox value={data.constraintSettings.poissonSolver.ringStackEnabled_2d ?? false}
						onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.ringStackEnabled_2d = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Use the ring-stack solve in 3D: K nested uniform-level rings (cell size c0·2^k)
						from the camera to the world edge, blended across the §3.2 band.  Makes the 50 km
						horizon stop looking like ~2500 m macro cells.  Default off (single working grid).
					`.AsMultiline(0)}>Ring-stack solve (3D):</TextPlus>
					<CheckBox value={data.constraintSettings.poissonSolver.ringStackEnabled_3d ?? false}
						onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.ringStackEnabled_3d = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Cells per side per ring ⇒ leaves/ring = this².  The cost dial (192 → ~37k leaves/ring).
						The max-working-grid-leaf-count clamp still bounds each ring's actual allocation.
					`.AsMultiline(0)}>Ring cells per side:</TextPlus>
					<Spinner min={8} step={8} value={data.constraintSettings.poissonSolver.ringCellsPerSide}
						onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.ringCellsPerSide = Math.max(4, Math.round(val)))}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Cell-size multiplier per ring.  Keep at 2 (the 2:1 coarsening + power-of-two snapping
						the solver depends on); exposed for experimentation only.
					`.AsMultiline(0)}>Ring growth factor:</TextPlus>
					<Spinner min={2} step={1} value={data.constraintSettings.poissonSolver.ringGrowthFactor}
						onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.ringGrowthFactor = Math.max(2, val))}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Width of the §3.2 blend band, in cells of the coarser ring.  Larger = smoother ring
						boundary, more dual-grid cost inside the band.  Default 3.
					`.AsMultiline(0)}>Ring blend band (cells):</TextPlus>
					<Spinner min={0} step={1} value={data.constraintSettings.poissonSolver.ringBlendBandCells}
						onChange={val=>Change(a=>a.constraintSettings.poissonSolver!.ringBlendBandCells = Math.max(0, Math.round(val)))}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Ring count override.  Blank = auto (ceil(log2(worldExtent / (mode3D_fineCellSize·ringCellsPerSide))) + 1,
						covering the world edge).  Set to force a specific count.
					`.AsMultiline(0)}>Ring count (0 = auto):</TextPlus>
					<Spinner min={0} step={1} value={data.constraintSettings.poissonSolver.ringCount}
						onChange2={val=>Change(a=>a.constraintSettings.poissonSolver!.ringCount = val != null && val > 0 ? Math.round(val) : undefined)}/>
				</RowLR>
			</>}

			{uiState.terrainDefinition_tab === TerrainDefinitionTab.shading && <>
				{/* ── Lighting ─────────────────────────────────────── */}
				<Row mt={8} style={{fontWeight: "bold"}}>
					<Text>Lighting:</Text>
					<Button ml="auto" text="Reset" onClick={()=>{
						if (!confirm("Reset lighting settings to defaults?")) return;
						Change(a=>{
							a.heightShadingConfig ??= new TerrainHeightShadingConfig();
							const cfg = a.heightShadingConfig;
							cfg.light_azimuth = 315; cfg.light_altitude = 45;
							cfg.ambient = 0.2; cfg.strength = 1.0;
						});
					}}/>
				</Row>
				{data.heightShadingConfig && <>
					<Row mt={3}>
						<TextPlus info={`
							The compass direction the light source comes FROM, in degrees clockwise from north.
							0° = light from north (shadows point south).
							90° = light from east (shadows point west).
							180° = light from south (shadows point north).
							270° = light from west (shadows point east).
							315° = light from northwest — the conventional default, matching how most printed topographic maps are shaded.
						`.AsMultiline(0)}>Light azimuth:</TextPlus>
						<Spinner min={0} max={360} value={data.heightShadingConfig.light_azimuth}
							onChange={val=>Change(a=>a.heightShadingConfig!.light_azimuth = val)}/>
						{(()=>{
							const changeRot = (delta: number)=>{
								Change(a=>{
									const cfg = a.heightShadingConfig!;
									cfg.light_azimuth = (cfg.light_azimuth + delta + 360) % 360;
								});
							};
							return <>
								<Button ml={5} p="2px 5px" text="-45" onClick={()=>changeRot(-45)}/>
								<Button ml={5} p="2px 5px" text="+45" onClick={()=>changeRot(45)}/>
							</>;
						})()}
						<TextPlus ml={8} info={`
							The angle of the light source above the horizon, in degrees.
							0° = light grazes the surface horizontally — maximum contrast, harsh shadows, very dramatic.
							45° = standard default, balances shadow detail with overall brightness.
							90° = light comes straight down (overhead/zenith) — no directional shadows; steep slopes go dark regardless of facing direction (slope-steepness shading).
						`.AsMultiline(0)}>Altitude:</TextPlus>
						<Spinner min={0} max={90} value={data.heightShadingConfig.light_altitude}
							onChange={val=>Change(a=>a.heightShadingConfig!.light_altitude = val)}/>
					</Row>
					<Row mt={3}>
						<TextPlus info={`
							The omnidirectional fill light intensity — the BRIGHTNESS OF THE SHADOWS (the floor illumination everything sits on).
							- 0 = shadows go fully black (maximum contrast against the sun).
							- 0.2 = the default; a modest dark-side floor.
							- 1 = no shadow darkening at all (everything flat-lit; direction becomes invisible).
							This is the "how dark do the shadows get" knob — NOT the sun's brightness (that's Sun intensity).
							Like Sun intensity, this is a physical AmbientLight.intensity (three.js) shared with the scenery bake.
						`.AsMultiline(0)}>Ambient:</TextPlus>
						<Slider min={0} max={3.5} step={0.01} {...sliderFlex} value={data.heightShadingConfig.ambient}
							onChange={val=>Change(a=>a.heightShadingConfig!.ambient = val)}/>
						<Spinner min={0} step={0.01} style={{width: 80}} value={data.heightShadingConfig.ambient}
							onChange={val=>Change(a=>a.heightShadingConfig!.ambient = val)}/>
					</Row>
					<Row mt={3}>
						<TextPlus info={`
							The directional (sun) light intensity — the BRIGHTNESS OF THE LIT (sun-facing) AREAS.
							- 0 = no sun at all; everything down to the Ambient floor (terrain reads uniformly dark/flat).
							- 1 = the default; a fully-bright sun.
							- >1 = over-bright sun (highlights blow out faster).
							This is the "how bright the bright side is" knob — the peak ALWAYS scales with it, independent of Ambient (which only sets the shadow floor). Set 0 to kill the sun; the terrain drops to flat Ambient illumination.
							This is used as the physical DirectionalLight.intensity (so values above 1 are valid), for both the terrain and scenery.
						`.AsMultiline(0)}>Sun intensity:</TextPlus>
						<Slider min={0} max={3.5} step={0.01} {...sliderFlex} value={data.heightShadingConfig.strength}
							onChange={val=>Change(a=>a.heightShadingConfig!.strength = val)}/>
						<Spinner min={0} step={0.01} style={{width: 80}} value={data.heightShadingConfig.strength}
							onChange={val=>Change(a=>a.heightShadingConfig!.strength = val)}/>
					</Row>
					{/* Set15 P2.5 (D3) — `slope_scale` spinner REMOVED.  The knob is retired (pinned to
					    1.0); the shader seam is retained internally so re-adding it is one line. */}
				</>}
				{/* ── Hypsometric color ramps ─────────────────────────── */}
				<Row mt={8} style={{fontWeight: "bold"}}>
					<Text>Hypsometric color ramps:</Text>
					<Button ml={5} mdIcon="plus" title="Add gradient" onClick={()=>Change(a=>{
						(a.heightColorGradients ??= []).push(new TerrainHeightColorGradient({name: `Gradient ${a.heightColorGradients.length + 1}`, isDefault: false}));
						RunInAction_Set(()=>uiState.terrainDefinition_selectedColorGradient_index = a.heightColorGradients.length - 1);
					})}/>
				</Row>
				{(()=>{
					const gradients = data.heightColorGradients;
					if (gradients == null) return null;
					const gi = Math.min(uiState.terrainDefinition_selectedColorGradient_index, gradients.length - 1);
					const gradient = gradients[gi];
					// Mutate the currently-selected gradient (a db-object field path) via ChangeMod.
					const ChangeGradient = (mutator: (g: TerrainHeightColorGradient)=>any)=>Change(a=>mutator(a.heightColorGradients[gi]));
					return <>
						<Row mt={3} style={{alignItems: "center"}}>
							<Text>Gradient:</Text>
							<Select ml={5} displayType="button bar"
								options={gradients.map((g, i)=>({name: g.name ? `#${i + 1} (${g.name})` : `#${i + 1}`, value: i}))}
								value={gi} onChange={val=>RunInAction_Set(()=>uiState.terrainDefinition_selectedColorGradient_index = val)}/>
							<Button ml={5} mdIcon="delete" title="Delete gradient" enabled={gradients.length > 1} onClick={()=>Change(a=>{
								if (!confirm("Delete this gradient?")) return;
								const wasDefault = a.heightColorGradients[gi]?.isDefault;
								a.heightColorGradients.splice(gi, 1);
								const newIdx = Math.min(gi, a.heightColorGradients.length - 1);
								RunInAction_Set(()=>uiState.terrainDefinition_selectedColorGradient_index = newIdx);
								// If we removed the default, promote the now-first entry to default.
								if (wasDefault && a.heightColorGradients.length > 0 && !a.heightColorGradients.some(g=>g.isDefault)) {
									a.heightColorGradients[0].isDefault = true;
								}
							})}/>
						</Row>
						{gradient && <>
							<Row mt={3} style={{alignItems: "center"}}>
								<Text>Name:</Text>
								<TextInput ml={5} value={gradient.name}
									onChange={val=>ChangeGradient(g=>g.name = val)}/>
								<CheckBox ml={5} text="Default" value={gradient.isDefault} onChange={val=>Change(a=>{
									// Enforce single-default: clear other flags, then set this one.
									for (const g of a.heightColorGradients) g.isDefault = false;
									a.heightColorGradients[gi].isDefault = val;
								})}/>
							</Row>
							<Row mt={5}>
								<Text>Stops:</Text>
								<Button ml={5} mdIcon="plus" onClick={()=>Change(a=>{
									const g = a.heightColorGradients[gi];
									const stops = g.stops;
									const lastElev = stops.length > 0 ? stops[stops.length - 1].elevation + 100 : 0;
									g.stops = [...stops, new HypsometricStop({elevation: lastElev, color: [128, 128, 128]})];
								})}/>
								<Button ml="auto" text="Reset stops" onClick={()=>{
									if (!confirm("Reset this gradient's stops to defaults?")) return;
									Change(a=>{
										const g = a.heightColorGradients[gi];
										if (g) g.stops = DEFAULT_HYPSOMETRIC_STOPS.map(s=>new HypsometricStop(s));
									});
								}}/>
							</Row>
							{gradient.stops.map((stop, idx)=>(
								<Row key={idx} mt={3} style={{alignItems: "center"}}>
									<Text>Elev:</Text>
									<Spinner ml={5} min={-Infinity}
										value={stop.elevation} onChange={val=>ChangeGradient(g=>g.stops[idx].elevation = val)}/>
									<ColorPickerBox_Ext ml={5}
										color={stop.color as [number,number,number]} onChange={val=>ChangeGradient(g=>g.stops[idx].color = [val[0], val[1], val[2]])}/>
									<Button ml="auto" mdIcon="content-copy" onClick={()=>Change(a=>{
										a.heightColorGradients[gi].stops.Insert(idx + 1, Clone(stop));
									})}/>
									<Button ml={5} mdIcon="delete" onClick={()=>Change(a=>{
										a.heightColorGradients[gi].stops = a.heightColorGradients[gi].stops.filter((_, i)=>i !== idx);
									})}/>
								</Row>
							))}
						</>}
					</>;
				})()}
			</>}
		</>
	);
});